import { Box, Typography } from '@mui/material';
import { get } from 'lodash';
import React from 'react';
import SummaryLine from 'src/components/ui-component/form-component/abschluss-page/SummaryLine';
import { useApplication } from 'src/context/ApplicationContext';
import { KOSTEN_ARTEN, NACHWEIS_POSITION_STATUS, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { sumNachweispositionen } from 'src/pages/private/online-antrag/formulas/mittelabruf/kreativ/executeKreativMAFormulas';
import { DisplayDecimal, DisplayEuroMUI } from 'src/utils/formats';

// import schemas
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import MittelabrufSchema from 'src/lib/paths/mittelabruf_FULL_Paths.json';
const MA_P = MittelabrufSchema.attributes;

export const MittelabrufKreativMA = () => {
	const { application } = useApplication();
	const nachweisPositionen = get(application, MA_P.Mittelabruf_Nachweispositions.path, []);

	const personalAngefallen = sumNachweispositionen(
		nachweisPositionen,
		KOSTEN_ARTEN.Personalkosten,
		NACHWEIS_POSITION_STATUS.bereitsAngefalleneAusgaben,
	);
	const sachAngefallen = sumNachweispositionen(nachweisPositionen, KOSTEN_ARTEN.Sachkosten, NACHWEIS_POSITION_STATUS.bereitsAngefalleneAusgaben);
	const personalGeplant = sumNachweispositionen(nachweisPositionen, KOSTEN_ARTEN.Personalkosten, NACHWEIS_POSITION_STATUS.geplant);
	const sachGeplant = sumNachweispositionen(nachweisPositionen, KOSTEN_ARTEN.Sachkosten, NACHWEIS_POSITION_STATUS.geplant);
	const foerderquote = get(application, MA_P.Foerderantrag.schema.attributes.Allgemein_Foerderquote_Gesamt.path, 0);
	const foerderung = Number(get(application, MA_P.Ermittlung_Des_Auszahlungsbetrages_Bewilligte_Zuwendung.path, 0)) || 0;
	const bereitsAusgezahlt = Number(get(application, MA_P.Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr.path, 0)) || 0;
	const verbleibendeFoerderung = Math.max(0, foerderung - bereitsAusgezahlt);
	const projektAusgabenAngefallen = personalAngefallen + sachAngefallen;
	const projektAusgabenGeplant = personalGeplant + sachGeplant;
	const zuwendungAnteilig = get(application, MA_P.Zuwendung_Anteilig_Gesamt.path, 0);
	const berechneteMaxFoerderung = get(application, MA_P.Ermittlung_Des_Auszahlungsbetrages_Anforderungsbetrag.path, 0);
	const verbleibendeFoerderungNachDiesemAbruf = Math.max(0, verbleibendeFoerderung - berechneteMaxFoerderung);
	const isNegative = verbleibendeFoerderung - zuwendungAnteilig < 0 ? true : false;
	return (
		<>
			<ApplicationHeading title='Mittelabruf' description={PLATZHALTER_TEXT} />
			{isNegative && (
				<MUIAlert
					infoType='warning'
					message='Die berechnete Förderung übersteigt die verbleibende Förderung. Es kann maximal die noch verfügbare Förderung ausgeschöpft werden.'
				/>
			)}
			<Typography variant='body1' sx={{ mb: 2, mt: 2 }}>
				Die Berechnung des Mittelabrufs basiert auf den von Ihnen gemachten Angaben, wird automatisch generiert und steht unter dem Vorbehalt
				einer fachlichen Prüfung durch die Hessen Agentur.
			</Typography>
			<Box sx={{ mb: 2 }}>
				<SummaryLine
					label='bis jetzt angefallene Projektausgaben'
					value={DisplayEuroMUI(projektAusgabenAngefallen)}
					boldLabel={true}
					alignValueRight={true}
				/>
				<SummaryLine
					label='Personalausgaben'
					value={DisplayEuroMUI(personalAngefallen)}
					alignValueRight={true}
					labelPadding={'0.75rem 0 0.75rem 2rem'}
					valueFontWeight={400}
				/>
				<SummaryLine label='Sachausgaben' value={DisplayEuroMUI(sachAngefallen)} alignValueRight={true} labelPadding={'0.75rem 0 0.75rem 2rem'} valueFontWeight={400} />
				<SummaryLine
					label='in den nächsten drei Monaten anfallende Projektausgaben'
					value={DisplayEuroMUI(projektAusgabenGeplant)}
					boldLabel={true}
					alignValueRight={true}
				/>
				<SummaryLine
					label='Personalausgaben'
					value={DisplayEuroMUI(personalGeplant)}
					alignValueRight={true}
					labelPadding={'0.75rem 0 0.75rem 2rem'}
					valueFontWeight={400}
				/>
				<SummaryLine label='Sachausgaben' value={DisplayEuroMUI(sachGeplant)} alignValueRight={true} labelPadding={'0.75rem 0 0.75rem 2rem'} valueFontWeight={400} />
				<SummaryLine label='Förderquote' value={`${DisplayDecimal(foerderquote)} %`} boldLabel={true} alignValueRight={true} />
				<SummaryLine label='Förderung (€)' value={DisplayEuroMUI(foerderung)} boldLabel={true} alignValueRight={true} />
				<SummaryLine
					label='Verfügbare Fördermittel nach diesem Mittelabruf (€)'
					value={DisplayEuroMUI(verbleibendeFoerderungNachDiesemAbruf)}
					boldLabel={true}
					alignValueRight={true}
				/>
				<SummaryLine
					label='berechnete max. Förderung / Mittelabruf (vorbehaltlich einer fachlichen Prüfung durch die Hessen Agentur)'
					value={DisplayEuroMUI(berechneteMaxFoerderung)}
					boldLabel={true}
					showBackground={true}
					alignValueRight={true}
				/>
			</Box>
			<Typography variant='body2' sx={{ mt: 2, display: 'block' }}>
				Ausgaben können nur bis zur im Förderbescheid festgesetzten Ausgabenhöhe als zuwendungsfähig anerkannt werden können. Mögliche
				Mehrausgaben gehen zu Lasten des Zuwendungsempfängers. Dieser Mittelabruf wurde automatisch durch das digitale Antragsportal der
				Innovationsförderung Hessen - basierend auf den von Ihnen gemachten Angaben - erstellt und steht unter dem Vorbehalt einer fachlichen
				Prüfung durch die Hessen Agentur.
			</Typography>
			<Typography variant='body2' sx={{ mt: 2, display: 'block' }}>
				<strong>Grundsätzlich gilt:</strong> Der Mittelabruf kann maximal in Höhe der bewilligten Förderung erfolgen.
			</Typography>
		</>
	);
};

export default MittelabrufKreativMA;
